import { ExternallinkIcon } from '@ordr-as/icons'
import { UnknownStatePaymentDto } from '../../../../generated-api'
import { RefreshButton } from '../RefreshButton'
import { Link } from 'react-router-dom'
import { formatDateAndTime } from 'utils/stringUtils'
import CollapsiblePanel from '../CollapsiblePanel'
import { usePaymentOperations, WatchedRowKind, WatchedRowStatus } from '../context/PaymentOperationsContext'
import { useSelectable } from '../hooks/useSelectable'
import { Button } from '@ordr-as/umami-react'
import { useCallback } from 'react'

export const UnknownStatePaymentsTable = ({
    unknownStatePayments,
}: {
    unknownStatePayments: UnknownStatePaymentDto[]
}) => {
    const { retryUnknown, isRefreshing, watchedRows } = usePaymentOperations()

    const canRetry = (payment: UnknownStatePaymentDto) => {
        if (payment.paymentId) {
            return true
        }

        return false
    }

    const { selected, isChecked, toggle, isAllChecked, toggleAll, isNonChecked, reset } = useSelectable(
        unknownStatePayments,
        {
            getId: (notification) => notification.paymentId,
            isSelectable: canRetry,
        }
    )

    const retryAll = useCallback(() => {
        const paymentIds = selected
        retryUnknown(paymentIds)
        reset()
    }, [reset, retryUnknown, selected])

    const getRowStatusClass = (id: number) => {
        const status = watchedRows.find((row) => row.id === id && row.kind === WatchedRowKind.psp)?.status

        const defaultClass = 'hover:bg-purple-lightest border-t border-grey-lightest cursor-pointer '
        if (!status) {
            return defaultClass
        }

        return status === WatchedRowStatus.processing
            ? defaultClass + 'bg-brand-accent'
            : defaultClass + 'bg-green-lightest'
    }

    const retryButtonText = isAllChecked ? 'Retry all' : 'Retry selected'

    return (
        <CollapsiblePanel
            title={`UNKNOWN State Payments (${unknownStatePayments?.length})`}
            defaultOpen={unknownStatePayments.length > 0}
        >
            <div>
                <div className="flex justify-end gap-4 px-5 pb-2">
                    <Button isDisabled={isNonChecked || isRefreshing} onClick={retryAll} kind="secondary">
                        {retryButtonText}
                    </Button>
                    <RefreshButton />
                </div>
                <div className="overflow-x-auto">
                    <table className="text-left min-w-full">
                        <thead className="text-grey-dark">
                            <tr>
                                <th className="py-2 px-5">
                                    <input type="checkbox" checked={isAllChecked} onChange={toggleAll} />
                                </th>
                                <th className="font-semibold py-2 px-5">Reference</th>
                                <th className="font-semibold py-2 px-5">Status</th>
                                <th className="font-semibold py-2 px-5">Events</th>
                                <th className="font-semibold py-2 px-5">Payment Method</th>
                                <th className="font-semibold py-2 px-5">Provider</th>
                                <th className="font-semibold py-2 px-5">Location ID</th>
                                <th className="font-semibold py-2 px-5">Created</th>
                                <th className="font-semibold py-2 px-5">Updated</th>
                                <th className="font-semibold py-2 px-5">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {unknownStatePayments.length ? (
                                unknownStatePayments.map((payment) => (
                                    <tr
                                        key={payment.paymentId}
                                        className={getRowStatusClass(payment.paymentId)}
                                        onClick={() => canRetry(payment) && toggle(payment)}
                                    >
                                        <td className="py-4 px-5">
                                            <input
                                                type="checkbox"
                                                disabled={!canRetry(payment)}
                                                checked={isChecked(payment)}
                                                onChange={() => toggle(payment)}
                                            />
                                        </td>
                                        <td className="py-4 px-5 text-sm">
                                            <Link
                                                to={'/payments/' + payment.paymentReference}
                                                className="text-brand-primary "
                                                target="_blank"
                                            >
                                                {payment.paymentReference}{' '}
                                                <ExternallinkIcon className="text-xs inline align-baseline" />
                                            </Link>
                                        </td>
                                        <td className="py-4 px-5 text-sm">UNKNOWN</td>
                                        <td className="py-4 px-5 text-sm text-center">{payment.events.length}</td>
                                        <td className="py-4 px-5 text-sm">{payment.paymentMethod}</td>
                                        <td className="py-4 px-5 text-sm">{payment.paymentProvider}</td>
                                        <td className="py-4 px-5 text-sm text-center">{payment.executingLocationId}</td>
                                        <td className="py-4 px-5 text-sm">{formatDateAndTime(payment.created)}</td>
                                        <td className="py-4 px-5 text-sm">{formatDateAndTime(payment.updated)}</td>
                                        <td className="py-4 px-5 text-sm">
                                            <Link
                                                to={`${payment.paymentReference}`}
                                                className="text-brand-primary mr-2"
                                            >
                                                Details
                                            </Link>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={10} className="py-4 px-5 text-center">
                                        No unknown state payments
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </CollapsiblePanel>
    )
}
